import { suggest_Check_str, suggest_CheckCircleFill_str, suggest_Close_str } from '@zenlayer/zen-design-icons';
import Dropdown from '../dropdown';
import SvgIcon from '../svg-icon';
import Tooltip from '../tooltip';
import { useCreation, useMemoizedFn } from 'ahooks';
import classnames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useConfig } from '../hooks/useConfig';
import { formatStringNoBreaks, formatValueText, isMatched } from './utils';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const getTextLabelValue = str => {
  var _value$trim;
  const [label, value] = str.split(/[:：](.*)/, 2);
  return {
    label: label.trim(),
    value: (_value$trim = value === null || value === void 0 ? void 0 : value.trim()) !== null && _value$trim !== void 0 ? _value$trim : ''
  };
};
const Item = props => {
  const {
    freeItems,
    focusId,
    isFocusWithin,
    placeholder,
    onChange,
    prefixClass
  } = props;
  const {
    locale: {
      SuperSearch: SuperSearchLocale
    }
  } = useConfig();
  const itemRef = useRef(null);
  const inputRef = useRef(null);
  const [inputFocus, setInputFocus] = useState(false);
  const [inputValue, setInputValue] = useState(props.value);
  const currentItem = useCreation(() => {
    const current = freeItems.find(item => {
      var _item$label;
      return inputValue === null || inputValue === void 0 ? void 0 : inputValue.toLowerCase().startsWith("".concat((_item$label = item.label) === null || _item$label === void 0 ? void 0 : _item$label.toLowerCase(), ":"));
    });
    if (current) {
      const valueText = formatValueText(getTextLabelValue(inputValue).value, current).toLowerCase();
      const optionLabels = current.multiple ? valueText.split('|').map(v => v.trim()) : [valueText];
      return {
        ...current,
        options: current.options ? current.options.map(option => {
          var _option$label;
          return {
            ...option,
            active: !option.disabled && optionLabels.includes((_option$label = option.label) === null || _option$label === void 0 ? void 0 : _option$label.toLowerCase())
          };
        }) : undefined
      };
    }
    return undefined;
  }, [freeItems, inputValue]);
  const memoizedSubmit = useMemoizedFn(() => {
    if (!currentItem) {
      return;
    }
    let value = '';
    switch (currentItem.mode) {
      case 'select':
        if (currentItem.multiple) {
          var _currentItem$options$, _currentItem$options;
          value = (_currentItem$options$ = (_currentItem$options = currentItem.options) === null || _currentItem$options === void 0 ? void 0 : _currentItem$options.reduce((acc, cur) => {
            if (cur.active) {
              acc.push(cur.value);
            }
            return acc;
          }, [])) !== null && _currentItem$options$ !== void 0 ? _currentItem$options$ : [];
        } else {
          var _currentItem$options$2, _currentItem$options2;
          value = (_currentItem$options$2 = (_currentItem$options2 = currentItem.options) === null || _currentItem$options2 === void 0 || (_currentItem$options2 = _currentItem$options2.find(opt => opt.active)) === null || _currentItem$options2 === void 0 ? void 0 : _currentItem$options2.value) !== null && _currentItem$options$2 !== void 0 ? _currentItem$options$2 : '';
        }
        break;
      default:
        value = getTextLabelValue(inputValue).value;
        break;
    }
    onChange('update', {
      ...currentItem,
      value
    });
  });
  const submit = () => {
    window.setTimeout(memoizedSubmit, 1);
  };
  const onEnter = useMemoizedFn(() => {
    if (currentItem) {
      // 已选择item
      let {
        value
      } = getTextLabelValue(inputValue);
      if (currentItem.options) {
        const valueText = formatValueText(value, currentItem).toLowerCase();
        const valueTextItems = currentItem.multiple ? valueText.split('|').map(it => it.trim().toLowerCase()) : [valueText];
        const realValueTextItems = currentItem.options.flatMap(item => {
          var _item$label2;
          return valueTextItems.includes((_item$label2 = item.label) === null || _item$label2 === void 0 ? void 0 : _item$label2.toLowerCase()) ? [item.label] : [];
        });
        value = realValueTextItems.join('|');
      } else {
        value = formatValueText(value, currentItem);
      }
      if (value) {
        setInputValue("".concat(currentItem.label, ": ").concat(value));
        submit();
      }
      return;
    }
    // 未选择item
    if (inputValue) {
      let matchedItem = freeItems.find(item => {
        return item.mode === 'text' && item.match && isMatched(item.match, inputValue, item.multiple);
      });
      if (!matchedItem) {
        matchedItem = freeItems.find(item => item.match === 'default');
      }
      if (matchedItem) {
        const valueText = matchedItem.multiple ? formatValueText(inputValue, matchedItem) : inputValue;
        setInputValue("".concat(matchedItem.label, ": ").concat(valueText));
        submit();
      }
    }
  });
  const dropdownRender = useCallback(() => {
    let content = [];
    if (currentItem) {
      const {
        mode,
        multiple,
        options = []
      } = currentItem;
      content = options.map(opt => {
        const {
          prefix,
          label,
          value,
          disabled,
          active,
          formatLabel
        } = opt;
        return /*#__PURE__*/_jsxs("div", {
          className: classnames('dropdown-item', {
            active,
            disabled: !!disabled
          }),
          onMouseDown: () => {
            if (disabled) {
              return;
            }
            if (mode === 'select' && !multiple) {
              if (active) {
                return;
              }
              setInputValue("".concat(currentItem.label, ": ").concat(label));
              submit();
            } else {
              const valueStr = options.reduce((acc, cur) => {
                if (cur.value === value) {
                  !cur.active && acc.push(cur.label);
                } else {
                  cur.active && acc.push(cur.label);
                }
                return acc;
              }, []).join('|');
              setInputValue("".concat(currentItem.label, ": ").concat(valueStr));
            }
          },
          children: [!!prefix && /*#__PURE__*/_jsx("div", {
            className: "dropdown-item--prefix",
            children: prefix
          }), /*#__PURE__*/_jsx("div", {
            className: "dropdown-item--label",
            children: formatLabel ? formatLabel() : label
          }), /*#__PURE__*/_jsx("div", {
            className: "dropdown-item--sufix",
            children: mode === 'select' && !multiple ? active ? /*#__PURE__*/_jsx(SvgIcon, {
              svgStr: suggest_Check_str
            }) : null : active ? /*#__PURE__*/_jsx(SvgIcon, {
              svgStr: suggest_CheckCircleFill_str
            }) : /*#__PURE__*/_jsx("i", {})
          })]
        }, value);
      });
    } else {
      content = /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("div", {
          className: "dropdown-title",
          children: SuperSearchLocale.filterDropdownLabel
        }), freeItems.map((_ref, idx) => {
          let {
            key,
            label,
            prefix,
            formatLabel
          } = _ref;
          return /*#__PURE__*/_jsxs("div", {
            className: "dropdown-item",
            onMouseDown: () => {
              setInputValue("".concat(label, ": "));
            },
            children: [!!prefix && /*#__PURE__*/_jsx("div", {
              className: "dropdown-item--prefix",
              children: prefix
            }), /*#__PURE__*/_jsx("div", {
              className: "dropdown-item--label",
              children: formatLabel ? formatLabel() : label
            })]
          }, key);
        })]
      });
    }
    return /*#__PURE__*/_jsx("div", {
      className: "".concat(prefixClass, "--dropdown"),
      onClick: e => {
        // 阻止点击事件冒泡
        e.preventDefault();
        e.stopPropagation();
      },
      onMouseDown: e => {
        // 防止点击下拉框时input失去焦点
        e.preventDefault();
        e.stopPropagation();
      },
      children: content
    });
  }, [freeItems, currentItem]);
  const _placeholder = useCreation(() => {
    if (!freeItems.length) {
      return '';
    }
    if (currentItem) {
      switch (currentItem.mode) {
        case 'select':
          return currentItem.multiple ? SuperSearchLocale.placeholder.select.multiple : SuperSearchLocale.placeholder.select.single;
        default:
          return currentItem.multiple ? SuperSearchLocale.placeholder.text.multiple : SuperSearchLocale.placeholder.text.single;
      }
    }
    let hasMultipleItem = false;
    let matchedItemLabels = [];
    freeItems.forEach(item => {
      if (item.match && !item.hideInPlaceholder) {
        matchedItemLabels.push(item.label);
        if (item.multiple) {
          hasMultipleItem = true;
        }
      }
    });
    if (inputFocus) {
      return hasMultipleItem ? SuperSearchLocale.placeholder.empty.focus.multiple : SuperSearchLocale.placeholder.empty.focus.single;
    }
    if (typeof placeholder === 'string') {
      return placeholder;
    }
    return matchedItemLabels.length ? SuperSearchLocale.placeholder.empty.blur + matchedItemLabels.join(SuperSearchLocale.placeholder.spliter) : SuperSearchLocale.placeholder.empty.blur.trim() + '...';
  }, [placeholder, freeItems, inputFocus, currentItem]);
  const showTag = !inputFocus && currentItem;
  useEffect(() => {
    setInputValue(props.value);
  }, [props.value]);
  useEffect(() => {
    if (focusId) {
      var _inputRef$current;
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
    }
  }, [focusId]);
  return /*#__PURE__*/_jsx(Dropdown, {
    open: inputFocus && isFocusWithin && freeItems.length > 0 && (currentItem ? currentItem.mode !== 'text' : true),
    dropdownRender: dropdownRender,
    overlayStyle: {
      minWidth: 200,
      transition: 'none'
    },
    children: /*#__PURE__*/_jsxs("div", {
      ref: itemRef,
      "data-super-search-content": showTag ? '1' : '0',
      className: classnames("".concat(prefixClass, "--item"), showTag ? 'content-tag' : 'content-input',
      // 必须使用css控制tag和input的显隐，否则focus-within会失效
      {
        hidden: !!isFocusWithin && !inputFocus && !inputValue
      }),
      onClick: e => {
        e.preventDefault();
        e.stopPropagation();
      },
      children: [/*#__PURE__*/_jsx(Tooltip, {
        trigger: ['hover'],
        title: SuperSearchLocale.tagHoverTip,
        children: /*#__PURE__*/_jsxs("div", {
          className: "".concat(prefixClass, "--tag"),
          children: [/*#__PURE__*/_jsx("div", {
            className: "".concat(prefixClass, "--tag--text"),
            onClick: () => {
              setInputFocus(true);
              window.setTimeout(() => {
                var _inputRef$current2;
                (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
              }, 0);
            },
            children: inputValue
          }), isFocusWithin && /*#__PURE__*/_jsx("div", {
            className: "".concat(prefixClass, "--tag--remove"),
            onMouseDown: e => {
              e.preventDefault();
              e.stopPropagation();
              onChange('remove');
            },
            children: /*#__PURE__*/_jsx(SvgIcon, {
              svgStr: suggest_Close_str,
              block: true
            })
          })]
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: "".concat(prefixClass, "--input"),
        children: [/*#__PURE__*/_jsx("div", {
          className: "".concat(prefixClass, "--input--mirror"),
          children: inputValue
        }), /*#__PURE__*/_jsxs("div", {
          className: "".concat(prefixClass, "--input--placeholder"),
          children: [!!inputValue && /*#__PURE__*/_jsx("div", {
            style: {
              visibility: 'hidden'
            },
            children: inputValue
          }), !!_placeholder && /*#__PURE__*/_jsx("div", {
            children: _placeholder
          })]
        }), /*#__PURE__*/_jsx("input", {
          ref: inputRef,
          value: inputValue,
          onChange: e => {
            setInputValue(e.target.value);
          },
          onPaste: e => {
            var _inputRef$current3, _inputRef$current4;
            e.preventDefault();
            const pasteValue = formatStringNoBreaks(e.clipboardData.getData('text'));
            const selectionStart = ((_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.selectionStart) || 0;
            const selectionEnd = ((_inputRef$current4 = inputRef.current) === null || _inputRef$current4 === void 0 ? void 0 : _inputRef$current4.selectionEnd) || 0;
            const newValue = inputValue.slice(0, selectionStart) + pasteValue + inputValue.slice(selectionEnd);
            setInputValue(newValue);
          },
          onFocus: () => {
            setInputFocus(true);
            if (inputValue.length) {
              window.setTimeout(() => {
                var _inputRef$current5;
                (_inputRef$current5 = inputRef.current) === null || _inputRef$current5 === void 0 || _inputRef$current5.setSelectionRange(inputValue.length, inputValue.length);
              });
            }
          },
          onBlur: () => {
            setInputValue(props.value);
            setInputFocus(false);
          },
          onKeyDown: e => {
            var _inputRef$current6;
            switch (e.key) {
              case 'Enter':
                e.preventDefault();
                onEnter();
                break;
              case 'Backspace':
                if (!inputValue) {
                  onChange('backspace');
                }
                break;
              case 'Escape':
                (_inputRef$current6 = inputRef.current) === null || _inputRef$current6 === void 0 || _inputRef$current6.blur();
                break;
              default:
                break;
            }
          }
        })]
      })]
    })
  });
};
export default Item;